import type React from 'react';
// The lib's own wrapper, not react-i18next directly — see MapPrintSection's
// note: a literal 'map' namespace fails to typecheck inside Viltrapport, whose
// i18next augmentation declares its own namespaces.
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { PAPER_FORMATS, pageMm, paperSize } from '../lib/printPaper';
import type { PaperFormat, PrintOrientation } from '../lib/printPaper';
import {
  MAX_PAGE_ASPECT,
  MIN_PAGE_ASPECT,
  pageAspect,
  pageForAspect,
  type PageMm,
} from '../lib/printCustomSheet';
import { Icon } from './Icon';
import { PRINT_LABEL_MM_MAX, PRINT_LABEL_MM_MIN, type PrintPinSizes } from '../lib/mapPrintPayload';

// ── Types ─────────────────────────────────────────────────────────────────

// The paper size table lives in `lib/printPaper`, where it is unit-testable
// without a DOM. Re-exported from here because this module has been the import
// site for it since the panel was written.
export type { PaperFormat, PrintOrientation };
export { PAPER_FORMATS };

export interface PrintPreviewPanelProps {
  format:              PaperFormat;
  orientation:         PrintOrientation;
  onFormatChange:      (f: PaperFormat) => void;
  onOrientationChange: (o: PrintOrientation) => void;
  onClose:             () => void;
  /**
   * The server-rendered PDF controls, when this surface offers them (see
   * `MapPrintSection`). Passed in as a node rather than as a bundle of print-job
   * props so that this panel — which is about paper, orientation and the browser
   * print — stays unaware of jobs, tokens and polling.
   */
  serverPrintSection?: React.ReactNode;
  /**
   * Pixels to drop the panel by, so it stacks beneath whatever the surface has
   * already put in the top-right column — today the hidden-pin-types chip.
   */
  topOffset?: number;
  /**
   * The custom sheet's millimetres, when `format === 'custom'`.
   *
   * TOTAL page, margins included — the number the user sets, the number the
   * button displays, and the number the aspect slider describes. The map area
   * inside it is `2 x DEFAULT_PRINT_MARGIN_MM` smaller on each axis.
   */
  customPage?: PageMm;
  onCustomPageChange?: (page: PageMm) => void;
  /**
   * True when this control is the one map zoom drives.
   *
   * Distinct from being SELECTED: the page and the scale can both be custom at
   * once, and then exactly one of them follows the camera while the other holds
   * its value. Selected colouring alone cannot say which, so the button carries
   * a second visual state.
   */
  customPageActive?: boolean;
  onActivateCustomPage?: () => void;
  /**
   * The width-over-height ratio the slider sits at, and the way it reports a
   * drag.
   *
   * The ratio is a value in its own right rather than something read back off
   * `customPage`: the page is rounded to whole millimetres, so its measured
   * ratio is never quite the one the slider was set to, and a thumb positioned
   * from it visibly creeps as the sheet is re-derived. Falls back to the page's
   * own ratio for a caller that tracks no ratio of its own.
   */
  aspect?: number;
  onAspectChange?: (aspect: number) => void;
  /**
   * Printed pin and label size in millimetres, and the pin slider's range (see
   * `usePrintPinSizes`). The sliders show only when `onPinSizesChange` is given:
   * they size what the server draws, so a surface without server print has
   * nothing for them to act on.
   */
  pinSizes?: PrintPinSizes;
  pinSizeRange?: { min: number; max: number };
  onPinSizesChange?: (next: PrintPinSizes) => void;
}

/** Millimetres for a slider readout — one decimal, dropped when it is zero. */
const mm = (value: number): string => `${Number(value.toFixed(1))} mm`;

// ── Panel ─────────────────────────────────────────────────────────────────

export function PrintPreviewPanel({
  format,
  orientation,
  onFormatChange,
  onOrientationChange,
  onClose,
  serverPrintSection,
  topOffset = 0,
  customPage,
  onCustomPageChange,
  customPageActive = false,
  onActivateCustomPage,
  aspect,
  onAspectChange,
  pinSizes,
  pinSizeRange,
  onPinSizesChange,
}: PrintPreviewPanelProps) {
  const { t } = useTranslation('map');
  // The chosen sheet in words — "A4 · Portrait".
  //
  // The paper used to be labelled on the crop frame itself, which was a DOM
  // rectangle drawn over the map. Now that the frame is map geometry there is
  // nowhere on it to hang text, so the label lives here instead, beside the
  // rest of the paper choice — derived from the same `format`/`orientation`
  // props the panel already has, rather than passed in twice by two hosts.
  //
  // A custom sheet has no format name and no orientation to name either, so it
  // labels itself with the millimetres the user set.
  const isCustom = format === 'custom' && customPage !== undefined;
  const customMm = customPage ? `${customPage.w} × ${customPage.h} mm` : '';
  const frameLabel = isCustom
    ? customMm
    : `${paperSize(format).label} · ${t(`serverPrint.${orientation}`)}`;
  return (
    <div
      data-map-panel="true"
      data-map-print="hide"
      style={{
        position: 'absolute',
        // Shares the top-right column with the hidden-pin-types chip, which the
        // host drops us below rather than letting the two overlap.
        top: 16 + topOffset,
        right: 60,
        width: 276,
        // Capped to the host, mirroring MapLayersPicker: on a short viewport the
        // panel used to run past the bottom edge with no way to reach the PDF
        // controls and the footer. The header and footer stay put; the body
        // below scrolls.
        maxHeight: `calc(100% - ${32 + topOffset}px)`,
        background: COLORS.surfaceContainerLowest,
        borderRadius: 12,
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        zIndex: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          minHeight: 52,
          flexShrink: 0,
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.onSurface }}>
            {t('serverPrint.previewTitle')}
          </div>
          <div
            style={{
              marginTop: 2,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: COLORS.onSurfaceVariant,
            }}
          >
            {frameLabel}
          </div>
        </div>
        <button className="wh-btn-icon" onClick={onClose} aria-label={t('serverPrint.close')}>
          <Icon name="x" size={18} />
        </button>
      </div>

      {/* Body */}
      <div
        data-testid="print-preview-body"
        style={{ padding: '14px 14px 10px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', flex: 1, minHeight: 0 }}
      >

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: 10, background: COLORS.primaryContainer }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <Icon name="map" size={16} color={COLORS.primary} />
            {/* Caller-agnostic on purpose: the action below the frame comes from
                `serverPrintSection` and a surface without server print offers none,
                but framing the shot is the same first step either way. */}
            <p style={{ margin: 0, fontSize: 12, color: COLORS.onSurface, lineHeight: 1.5 }}>
              {t('serverPrint.composeHint')}
            </p>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.outline, marginBottom: 8 }}>
            {t('serverPrint.paperFormat')}
          </div>
          {/* Five two-character A-labels fit a 276px panel on one row whichever
              way they are sized, so the content sizing here is for the widths
              this panel does not control: a browser zoom, a narrowed sidebar, or
              a longer label. Growing from content rather than splitting the row
              equally means a label is never given less room than its text needs,
              and wrap is what happens instead of truncation when the row runs
              out of width. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PAPER_FORMATS.map((f) => (
              <button
                key={f.id}
                onClick={() => onFormatChange(f.id)}
                style={{
                  flex: '1 1 auto', minWidth: 32, padding: '0 6px',
                  height: 32, borderRadius: 8, cursor: 'pointer',
                  border: `1.5px solid ${format === f.id ? COLORS.primary : COLORS.outlineVariant}`,
                  background: format === f.id ? COLORS.primaryContainer : 'transparent',
                  color: format === f.id ? COLORS.primary : COLORS.onSurface,
                  fontSize: 13, fontWeight: 600,
                  transition: 'border-color 0.12s, background 0.12s, color 0.12s',
                }}
              >
                {f.label}
              </button>
            ))}
            {/* Custom sits in the same row as the named formats because it is
                the same choice — which sheet — not a mode switch. It shows the
                millimetres once chosen, so the row states the answer rather
                than just the question. */}
            <button
              // Named explicitly only while it reads "Custom", because the scale
              // row's button says exactly the same word and the two sit in one
              // dialog. Once a sheet is chosen the millimetres on its face are
              // already unique, and a label would then hide them.
              aria-label={isCustom ? undefined : t('serverPrint.customPaper')}
              data-active={isCustom && customPageActive ? 'true' : undefined}
              onClick={() => {
                // Seed from the sheet that was showing — its RATIO is what
                // survives, since activating a control is the user asking zoom
                // to size it and the host re-derives it in the same tick (see
                // `useSheetFromCamera`). Only when arriving: a second click on
                // an already-custom sheet is the user making it the active
                // control, and must not reset their millimetres.
                if (format !== 'custom') {
                  onFormatChange('custom');
                  onCustomPageChange?.(pageMm(format, orientation));
                }
                onActivateCustomPage?.();
              }}
              style={{
                flex: '1 1 auto', minWidth: 32, padding: '0 6px',
                height: 32, borderRadius: 8, cursor: 'pointer',
                border: `1.5px solid ${format === 'custom' ? COLORS.primary : COLORS.outlineVariant}`,
                // The active control gets a ring OUTSIDE its border rather than a
                // different border colour: selected already owns the border, and
                // the two states have to be legible at once.
                outline: isCustom && customPageActive ? `2px solid ${COLORS.primary}` : undefined,
                outlineOffset: 1,
                background: format === 'custom' ? COLORS.primaryContainer : 'transparent',
                color: format === 'custom' ? COLORS.primary : COLORS.onSurface,
                fontSize: 13, fontWeight: 600,
                transition: 'border-color 0.12s, background 0.12s, color 0.12s',
              }}
            >
              {/* Prefixed, because this pill states a value the MAP keeps
                  changing while its neighbours state fixed choices — and
                  because a custom sheet's millimetres can land on a named
                  format's, leaving two pills reading the same thing with no
                  way to tell which is which. */}
              {isCustom ? `${t('serverPrint.autoPrefix')} ${customMm}` : t('serverPrint.custom')}
            </button>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.outline, marginBottom: 8 }}>
            {isCustom ? t('serverPrint.aspectRatio') : t('serverPrint.orientation')}
          </div>
          {/* The slider REPLACES portrait/landscape rather than joining them: a
              custom sheet states both dimensions directly, so there is nothing
              left for an orientation to swap. */}
          {isCustom ? (
            <input
              type="range"
              aria-label={t('serverPrint.aspectRatio')}
              // Logarithmic, so the travel is symmetric: a ratio and its
              // reciprocal are the same distance from the middle, and the
              // SQUARE sits in the middle rather than squeezed against the left
              // end. On a linear scale the whole of portrait — every sheet
              // narrower than it is tall — lived in the first fifth of the
              // track, which made the useful half of the range the hard half to
              // aim at.
              min={Math.log2(MIN_PAGE_ASPECT)}
              max={Math.log2(MAX_PAGE_ASPECT)}
              step={0.01}
              value={Math.log2(aspect ?? pageAspect(customPage))}
              onChange={(e) => {
                const next = 2 ** Number(e.target.value);
                onAspectChange?.(next);
                // Also reported as a page, for a host that tracks no ratio of
                // its own — `onAspectChange`'s own implementation reshapes the
                // page too, so a host wiring both gets one answer, not two.
                if (!onAspectChange) onCustomPageChange?.(pageForAspect(customPage, next));
              }}
              style={{ width: '100%' }}
            />
          ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            {(['portrait', 'landscape'] as PrintOrientation[]).map((o) => (
              <button
                key={o}
                onClick={() => onOrientationChange(o)}
                style={{
                  flex: 1, height: 32, borderRadius: 8, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  border: `1.5px solid ${orientation === o ? COLORS.primary : COLORS.outlineVariant}`,
                  background: orientation === o ? COLORS.primaryContainer : 'transparent',
                  color: orientation === o ? COLORS.primary : COLORS.onSurface,
                  fontSize: 13, fontWeight: 600,
                  transition: 'border-color 0.12s, background 0.12s, color 0.12s',
                }}
              >
                <svg width={o === 'portrait' ? 10 : 14} height={o === 'portrait' ? 14 : 10} viewBox="0 0 10 14" style={{ flexShrink: 0 }}>
                  <rect
                    x="0.5" y="0.5"
                    width={o === 'portrait' ? 9 : 13}
                    height={o === 'portrait' ? 13 : 9}
                    rx="1" fill="none" stroke="currentColor" strokeWidth="1.2"
                  />
                </svg>
                {t(`serverPrint.${o}`)}
              </button>
            ))}
          </div>
          )}
        </div>

        {pinSizes && onPinSizesChange && (
          <>
            <PrintSizeSlider
              label={t('serverPrint.pinSize')}
              readout={mm(pinSizes.pinMm)}
              min={pinSizeRange?.min ?? pinSizes.pinMm}
              max={pinSizeRange?.max ?? pinSizes.pinMm}
              value={pinSizes.pinMm}
              onChange={(pinMm) => onPinSizesChange({ ...pinSizes, pinMm })}
            />
            <PrintSizeSlider
              label={t('serverPrint.labelSize')}
              // 0 is the off position rather than a size: a label too small to
              // read is no label at all.
              readout={pinSizes.labelMm > 0 ? mm(pinSizes.labelMm) : t('serverPrint.labelsOff')}
              min={PRINT_LABEL_MM_MIN}
              max={PRINT_LABEL_MM_MAX}
              value={pinSizes.labelMm}
              onChange={(labelMm) => onPinSizesChange({ ...pinSizes, labelMm })}
            />
          </>
        )}

        {serverPrintSection && (
          <div
            style={{
              borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
              paddingTop: 14,
            }}
          >
            {serverPrintSection}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 14px', borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
        {/* Cancel alone: every surface that opens this panel prints through
            `serverPrintSection`'s own "Create PDF". The browser print pass this
            footer used to start is gone — the map is a MapLibre canvas now, which
            a print stylesheet cannot re-rasterise for paper. */}
        <button className="wh-btn wh-btn-outline" onClick={onClose}>{t('serverPrint.cancel')}</button>
      </div>
    </div>
  );
}

function PrintSizeSlider({
  label,
  readout,
  min,
  max,
  value,
  onChange,
}: {
  label: string;
  readout: string;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.outline }}>
          {label}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.onSurface }}>{readout}</div>
      </div>
      <input
        type="range"
        aria-label={label}
        aria-valuetext={readout}
        min={min}
        max={max}
        // Fine enough that a default of 7.5 x an arbitrary Pin size sits on the
        // track where it is, rather than snapping when first drawn.
        step={0.1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%' }}
      />
    </div>
  );
}
